/**
 * Success Story Card V2 - Elegant Collapsible Design with Unified Metric Box
 * Single gradient box for metrics matching Dine AI Impact Dashboard style
 * Color-coded gradients: Green (Revenue), Blue (Time), Purple (Success)
 */

import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, TrendingUp, Clock, Trophy, Sparkles, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

interface SuccessStoryCardProps {
  type: 'revenue' | 'time' | 'competition' | 'phone';
  restaurantName: string;
  ownerName: string;
  location: string;
  beforeMetric: string | number;
  afterMetric: string | number;
  metricLabel?: string;
  headline: string;
  quote: string;
  impact: string;
  actionLabel: string;
  onActionClick: () => void;
  index: number;
  ownerImage?: string;
}

const iconMap = {
  revenue: TrendingUp,
  time: Clock,
  competition: Trophy,
  phone: Phone
};

const badgeLabels = {
  revenue: 'Revenue Story',
  time: 'Time Story',
  competition: 'Success Story',
  phone: 'Virtual Host Story'
};

// Color gradients matching Dine AI Impact Dashboard
const gradientMap = {
  revenue: 'from-emerald-500/20 via-green-500/15 to-teal-500/20', // Green shades
  time: 'from-blue-500/20 via-indigo-500/15 to-purple-500/20',    // Blue to purple
  competition: 'from-purple-500/20 via-pink-500/15 to-rose-500/20', // Purple to pink
  phone: 'from-orange-500/20 via-amber-500/15 to-yellow-500/20'   // Orange to yellow for phone/VH
};

const borderColorMap = {
  revenue: 'border-emerald-500/30',
  time: 'border-blue-500/30',
  competition: 'border-purple-500/30',
  phone: 'border-orange-500/30'
};

// Badge colors to match the gradient boxes
const badgeColorMap = {
  revenue: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    icon: 'text-emerald-400'
  },
  time: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    icon: 'text-blue-400'
  },
  competition: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    icon: 'text-purple-400'
  },
  phone: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    icon: 'text-orange-400'
  }
};

export const SuccessStoryCardV2 = ({
  type,
  restaurantName,
  ownerName,
  location,
  beforeMetric,
  afterMetric,
  metricLabel,
  headline,
  quote,
  impact,
  actionLabel,
  onActionClick,
  index,
  ownerImage
}: SuccessStoryCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = iconMap[type];
  const badgeLabel = badgeLabels[type];
  const gradient = gradientMap[type];
  const borderColor = borderColorMap[type];
  const badgeColors = badgeColorMap[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.15,
        ease: "easeOut"
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group h-full"
    >
      <div className="relative rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-yellow-400/30 hover:border-yellow-400/50 group-hover:-translate-y-1 min-h-[320px] flex flex-col backdrop-blur-sm" style={{ backgroundColor: '#3256dc' }}>
        
        {/* Badge - Color matched to gradient box */}
        <div className={`inline-block ${badgeColors.bg} px-3 py-1 rounded-full mb-4 self-start`}>
          <span className={`${badgeColors.text} font-medium text-xs flex items-center gap-1`}>
            <Sparkles className={`w-3 h-3 ${badgeColors.icon}`} />
            {badgeLabel}
          </span>
        </div>

        {/* Headline */}
        <h3 className="text-white font-bold text-lg mb-4">
          {headline}
        </h3>

        {/* Unified Metric Box with Gradient Background */}
        <motion.div 
          className={`bg-gradient-to-br ${gradient} backdrop-blur-sm rounded-xl p-4 border ${borderColor} mb-4`}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3, type: "spring", stiffness: 150 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Before */}
            <div className="text-center flex-1">
              <div className="text-white/60 text-xs mb-1">Before</div>
              <div className="text-white/90 text-xl font-bold">{beforeMetric}</div>
            </div>
            
            {/* Arrow */}
            <motion.div 
              animate={{ x: isHovered ? [0, 5, 0] : 0 }}
              transition={{ duration: 0.6, repeat: isHovered ? Infinity : 0, repeatType: "loop" }}
              className="flex-shrink-0"
            >
              <ArrowRight className="w-5 h-5 text-dineai-yellow" />
            </motion.div>
            
            {/* After - Emphasized */}
            <div className="text-center flex-1">
              <div className="text-white/60 text-xs mb-1">After</div>
              <motion.div 
                className="text-dineai-yellow text-4xl font-bold"
                initial={{ scale: 1 }}
                whileInView={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                {afterMetric}
              </motion.div>
            </div>
          </div>
          
          {/* Metric Label */}
          {metricLabel && (
            <p className="text-white/70 text-xs text-center mt-3 pt-3 border-t border-white/10">
              {metricLabel}
            </p>
          )}
        </motion.div>

        {/* Collapsible Content - Shows on Hover */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="space-y-3 overflow-hidden"
            >
              {/* Quote */}
              <motion.blockquote 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-white/80 text-sm italic leading-relaxed border-l-2 border-dineai-yellow/50 pl-3"
              >
                "{quote}"
              </motion.blockquote>

              {/* Impact */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="flex items-center gap-2 bg-white/5 rounded-lg p-2"
              >
                <Icon className="w-4 h-4 text-dineai-yellow" />
                <span className="text-white/90 text-sm font-semibold">{impact}</span>
              </motion.div>

              {/* Owner Info */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-center gap-3 pt-2 border-t border-white/10"
              >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  {ownerImage ? (
                    <img src={ownerImage} alt={ownerName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white/60 text-xs font-bold">
                      {ownerName.split(' ').map(n => n[0]).join('')}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">- {ownerName}</p>
                  <p className="text-white/60 text-xs">{restaurantName}, {location}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA - Always at Bottom */}
        <div className="mt-auto pt-4 text-center">
          <button
            onClick={onActionClick}
            className="text-dineai-yellow hover:text-yellow-300 text-sm font-semibold transition-all duration-200 inline-flex items-center gap-1 group/btn hover:gap-2"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Hover Indicator - Shows when not hovered */}
        {!isHovered && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-2 right-2"
          >
            <span className="text-white/40 text-[10px] italic">Hover for details</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};